<script>
import { mapGetters } from 'vuex';
import InboxesAPI from 'dashboard/api/inboxes';
import VoiceLinesAPI from 'dashboard/api/voiceLines';
import { pickDefaultVoiceLine } from 'dashboard/helper/voiceLines';
import { useAlert } from 'dashboard/composables';
import { INBOX_TYPES } from 'shared/mixins/inboxMixin';
import { REDIS_FEATURE_FLAGS } from 'dashboard/featureFlags';
import { isOnOnboardingView } from 'v3/helpers/RouteHelper';
import { isAttendanceRoute } from 'dashboard/helper/voiceSoftphoneScope';
import {
  isCallRunningIn,
  holdVoiceSession,
  releaseVoiceSession,
} from 'dashboard/helper/voiceCallSession';
import { BUS_EVENTS } from 'shared/constants/busEvents';

export const SOFTPHONE_URL = 'https://voip.atendesimples.com/';
export const SOFTPHONE_WINDOW = {
  NAME: 'atendesimples-softphone',
  FEATURES: 'popup,width=420,height=720',
};
export const DOCK_LAYOUT = {
  DEFAULT_WIDTH: 384,
  DEFAULT_HEIGHT: 640,
  MIN_WIDTH: 320,
  MAX_WIDTH: 720,
  MIN_HEIGHT: 480,
  EDGE_PADDING: 8,
  LAUNCHER_CLEARANCE: 112,
  STORAGE_KEY: 'voiceSoftphoneDock.layout',
  PERSIST_DEBOUNCE_MS: 250,
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const viewport = () => ({
  width: window.innerWidth,
  height: window.innerHeight,
});

export const clampLayout = layout => {
  const { width: vw, height: vh } = viewport();
  const { EDGE_PADDING, MIN_WIDTH, MAX_WIDTH, MIN_HEIGHT } = DOCK_LAYOUT;
  const width = clamp(
    layout.width,
    MIN_WIDTH,
    Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, vw - EDGE_PADDING * 2))
  );
  const height = clamp(
    layout.height,
    MIN_HEIGHT,
    Math.max(MIN_HEIGHT, vh - EDGE_PADDING * 2)
  );
  return {
    width,
    height,
    left: clamp(
      layout.left,
      EDGE_PADDING,
      Math.max(EDGE_PADDING, vw - width - EDGE_PADDING)
    ),
    bottom: clamp(
      layout.bottom,
      EDGE_PADDING,
      Math.max(EDGE_PADDING, vh - height - EDGE_PADDING)
    ),
  };
};

export const defaultLayout = () => {
  const { DEFAULT_WIDTH, DEFAULT_HEIGHT, EDGE_PADDING, LAUNCHER_CLEARANCE } =
    DOCK_LAYOUT;
  return clampLayout({
    width: DEFAULT_WIDTH,
    height: DEFAULT_HEIGHT,
    left: viewport().width - DEFAULT_WIDTH - EDGE_PADDING * 3,
    bottom: LAUNCHER_CLEARANCE,
  });
};

const readStoredLayout = () => {
  try {
    const raw = localStorage.getItem(DOCK_LAYOUT.STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    const isNumber = value => typeof value === 'number' && !Number.isNaN(value);
    if (
      parsed &&
      ['width', 'height', 'left', 'bottom'].every(key => isNumber(parsed[key]))
    ) {
      return clampLayout({
        ...parsed,
        bottom: Math.max(parsed.bottom, DOCK_LAYOUT.LAUNCHER_CLEARANCE),
      });
    }
  } catch (error) {
    return null;
  }
  return null;
};

export const SOFTPHONE_LOAD_TIMEOUT_MS = 10000;

const MANUAL_FALLBACK = { url: SOFTPHONE_URL, mode: 'manual', reason: 'error' };

export const isSoftphoneFallback = softphone => softphone?.reason === 'error';

const requestWithTimeout = async (request, fallback) => {
  let timer;
  const expired = new Promise((resolve, reject) => {
    timer = setTimeout(
      () => reject(new Error('softphone_timeout')),
      SOFTPHONE_LOAD_TIMEOUT_MS
    );
  });
  request.catch(() => {});
  try {
    const { data } = await Promise.race([request, expired]);
    return data;
  } catch (error) {
    return fallback();
  } finally {
    clearTimeout(timer);
  }
};

export const fetchSoftphone = inboxId =>
  requestWithTimeout(InboxesAPI.getVoiceSoftphone(inboxId), () => ({
    ...MANUAL_FALLBACK,
  }));

export const fetchVoiceLines = async () => {
  const data = await requestWithTimeout(VoiceLinesAPI.get(), () => null);
  return Array.isArray(data?.payload) ? data.payload : [];
};

const detachedWindow = { current: null, inboxId: null };

export const hasDetachedSoftphoneWindow = () =>
  Boolean(detachedWindow.current && !detachedWindow.current.closed);

export const focusDetachedSoftphoneWindow = () => {
  if (hasDetachedSoftphoneWindow()) detachedWindow.current.focus();
};

export const forgetDetachedSoftphoneWindow = () => {
  detachedWindow.current = null;
  detachedWindow.inboxId = null;
};

export const closeDetachedSoftphoneWindow = () => {
  if (hasDetachedSoftphoneWindow()) detachedWindow.current.close();
  forgetDetachedSoftphoneWindow();
};

export const pruneDetachedSoftphoneWindow = inboxIds => {
  if (!hasDetachedSoftphoneWindow()) return;
  if (!inboxIds.includes(detachedWindow.inboxId)) {
    closeDetachedSoftphoneWindow();
  }
};

export const openSoftphoneWindow = (url, inboxId) => {
  const popup = window.open(
    url,
    SOFTPHONE_WINDOW.NAME,
    SOFTPHONE_WINDOW.FEATURES
  );
  if (!popup) return null;
  popup.opener = null;
  detachedWindow.current = popup;
  detachedWindow.inboxId = inboxId;
  return popup;
};

export default {
  name: 'VoiceSoftphoneDock',
  data() {
    return {
      isOpen: false,
      isFrameMounted: false,
      isLoading: false,
      softphone: null,
      softphoneInboxId: null,
      layout: readStoredLayout() || defaultLayout(),
      isMoving: false,
      isResizing: false,
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      currentUserId: 'getCurrentUserID',
      inboxes: 'inboxes/getInboxes',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
    }),
    voiceInbox() {
      return this.inboxes.find(
        inbox => inbox.channel_type === INBOX_TYPES.VOICE
      );
    },
    voiceInboxIds() {
      return this.inboxes
        .filter(inbox => inbox.channel_type === INBOX_TYPES.VOICE)
        .map(inbox => inbox.id);
    },
    isEligible() {
      return (
        !!this.accountId &&
        this.isFeatureEnabledonAccount(
          this.accountId,
          REDIS_FEATURE_FLAGS.CHANNEL_VOICE
        ) &&
        Boolean(this.voiceInbox)
      );
    },
    isOnSoftphonePage() {
      return Boolean(this.$route?.meta?.voiceSoftphonePage);
    },
    isOnAttendanceScreen() {
      return isAttendanceRoute(this.$route);
    },
    canShowDock() {
      return (
        this.isEligible &&
        !isOnOnboardingView(this.$route) &&
        !this.isOnSoftphonePage &&
        this.isOnAttendanceScreen
      );
    },
    softphoneUrl() {
      return this.softphone?.url || SOFTPHONE_URL;
    },
    hintKey() {
      if (this.softphone?.mode === 'token') return 'VOICE_SOFTPHONE.HINT_TOKEN';
      if (this.softphone?.reason === 'no_extension')
        return 'VOICE_SOFTPHONE.HINT_NO_EXTENSION';
      return 'VOICE_SOFTPHONE.HINT';
    },
    hintParams() {
      return { extension: this.softphone?.extension || '' };
    },
    isFallback() {
      return isSoftphoneFallback(this.softphone);
    },
    launcherLabel() {
      return this.$t('VOICE_SOFTPHONE.TOGGLE');
    },
    panelStyle() {
      const { width, height, left, bottom } = this.layout;
      return {
        width: `${width}px`,
        height: `${height}px`,
        left: `${left}px`,
        bottom: `${bottom}px`,
      };
    },
    isInteracting() {
      return this.isMoving || this.isResizing;
    },
  },
  watch: {
    isOnSoftphonePage(onPage) {
      if (onPage && !isCallRunningIn('dock')) this.closeSession();
    },
    isFrameMounted(mounted) {
      if (mounted) holdVoiceSession('dock');
      else releaseVoiceSession('dock');
    },
    isOnAttendanceScreen(onScreen) {
      if (!onScreen) this.isOpen = false;
    },
    accountId() {
      this.resetSession();
    },
    voiceInboxIds(inboxIds) {
      if (this.softphoneInboxId && !inboxIds.includes(this.softphoneInboxId)) {
        this.resetSession({ closeWindow: false });
      }
      pruneDetachedSoftphoneWindow(inboxIds);
    },
  },
  mounted() {
    window.addEventListener('resize', this.reclampLayout);
    this.$emitter.on(BUS_EVENTS.VOICE_CALL_ENDED, this.onVoiceCallEnded);
    this.$emitter.on(BUS_EVENTS.VOICE_SOFTPHONE_OPEN, this.onOpenRequested);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.reclampLayout);
    this.$emitter.off(BUS_EVENTS.VOICE_CALL_ENDED, this.onVoiceCallEnded);
    this.$emitter.off(BUS_EVENTS.VOICE_SOFTPHONE_OPEN, this.onOpenRequested);
    releaseVoiceSession('dock');
    this.stopPointerTracking();
    clearTimeout(this.$_persistTimer);
  },
  methods: {
    onOpenRequested() {
      if (this.isEligible) this.isOpen = true;
    },
    onVoiceCallEnded() {
      if (this.isOnSoftphonePage && !isCallRunningIn('dock')) {
        this.closeSession();
      }
    },
    closeSession() {
      this.isOpen = false;
      this.isFrameMounted = false;
    },
    resetSession({ closeWindow = true } = {}) {
      this.closeSession();
      this.$_loadSequence = (this.$_loadSequence || 0) + 1;
      this.isLoading = false;
      this.softphone = null;
      this.softphoneInboxId = null;
      if (closeWindow) closeDetachedSoftphoneWindow();
    },
    async toggle() {
      if (hasDetachedSoftphoneWindow()) {
        focusDetachedSoftphoneWindow();
        useAlert(this.$t('VOICE_SOFTPHONE.WINDOW_ALREADY_OPEN'));
        return;
      }
      this.isOpen = !this.isOpen;
      if (!this.isOpen || this.isFrameMounted) return;
      const ready = await this.loadSoftphone();
      if (ready && this.isOpen && !hasDetachedSoftphoneWindow()) {
        this.isFrameMounted = true;
      }
    },
    async resolveSoftphoneInboxId() {
      const lines = await fetchVoiceLines();
      const line = pickDefaultVoiceLine(
        lines.filter(item => this.voiceInboxIds.includes(item.inbox_id)),
        this.accountId,
        this.currentUserId
      );
      return line ? line.inbox_id : this.voiceInbox?.id || null;
    },
    async loadSoftphone() {
      if (!this.voiceInbox) return false;
      this.$_loadSequence = (this.$_loadSequence || 0) + 1;
      const sequence = this.$_loadSequence;
      this.isLoading = true;
      const inboxId = await this.resolveSoftphoneInboxId();
      if (sequence !== this.$_loadSequence) return false;
      if (!inboxId) {
        this.isLoading = false;
        return false;
      }
      if (
        this.softphone &&
        !isSoftphoneFallback(this.softphone) &&
        this.softphoneInboxId === inboxId
      ) {
        this.isLoading = false;
        return true;
      }
      if (this.softphoneInboxId !== inboxId) this.softphone = null;
      this.softphoneInboxId = inboxId;
      const result = await fetchSoftphone(inboxId);
      if (sequence !== this.$_loadSequence) return false;
      this.softphone = result;
      this.isLoading = false;
      return true;
    },
    async retryLogin() {
      this.isFrameMounted = false;
      this.softphone = null;
      const ready = await this.loadSoftphone();
      if (ready && this.isOpen && !hasDetachedSoftphoneWindow()) {
        this.isFrameMounted = true;
      }
    },
    openInWindow() {
      if (!this.softphone) return;
      if (isCallRunningIn('dock')) {
        useAlert(this.$t('VOICE_SOFTPHONE.WINDOW_BLOCKED_ON_CALL'));
        return;
      }
      if (!openSoftphoneWindow(this.softphoneUrl, this.softphoneInboxId)) {
        useAlert(this.$t('VOICE_SOFTPHONE.POPUP_BLOCKED'));
        return;
      }
      this.closeSession();
    },
    reclampLayout() {
      this.applyLayout(this.layout);
    },
    resetLayout() {
      this.applyLayout(defaultLayout());
    },
    applyLayout(next) {
      this.layout = clampLayout(next);
      this.schedulePersist();
    },
    schedulePersist() {
      clearTimeout(this.$_persistTimer);
      this.$_persistTimer = setTimeout(
        this.persistLayout,
        DOCK_LAYOUT.PERSIST_DEBOUNCE_MS
      );
    },
    persistLayout() {
      try {
        localStorage.setItem(
          DOCK_LAYOUT.STORAGE_KEY,
          JSON.stringify(this.layout)
        );
      } catch (error) {
        this.$_persistError = error;
      }
    },
    startMove(event) {
      if (event.button !== 0) return;
      event.preventDefault();
      const origin = { x: event.clientX, y: event.clientY, ...this.layout };
      this.trackPointer(move => {
        this.applyLayout({
          ...this.layout,
          left: origin.left + (move.clientX - origin.x),
          bottom: origin.bottom - (move.clientY - origin.y),
        });
      });
      this.isMoving = true;
    },
    startResize(event) {
      if (event.button !== 0) return;
      event.preventDefault();
      const origin = { x: event.clientX, y: event.clientY, ...this.layout };
      this.trackPointer(move => {
        const deltaX = move.clientX - origin.x;
        const width = clamp(
          origin.width - deltaX,
          DOCK_LAYOUT.MIN_WIDTH,
          DOCK_LAYOUT.MAX_WIDTH
        );
        this.applyLayout({
          ...this.layout,
          width,
          height: origin.height - (move.clientY - origin.y),
          left: origin.left + (origin.width - width),
        });
      });
      this.isResizing = true;
    },
    trackPointer(onMove) {
      this.detachPointerHandlers();
      const onUp = () => this.stopPointerTracking();
      this.$_pointerHandlers = { onMove, onUp };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    },
    detachPointerHandlers() {
      const handlers = this.$_pointerHandlers;
      if (!handlers) return;
      window.removeEventListener('pointermove', handlers.onMove);
      window.removeEventListener('pointerup', handlers.onUp);
      window.removeEventListener('pointercancel', handlers.onUp);
      this.$_pointerHandlers = null;
    },
    stopPointerTracking() {
      this.detachPointerHandlers();
      this.isMoving = false;
      this.isResizing = false;
    },
  },
};
</script>

<template>
  <div v-show="canShowDock" class="voice-softphone-dock">
    <template v-if="isEligible">
      <div
        v-show="isOpen"
        class="voice-softphone-dock__panel relative flex flex-col overflow-hidden rounded-3xl border border-slate-100 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-md"
        :class="{ 'select-none': isInteracting }"
        :style="panelStyle"
        data-testid="softphone-panel"
      >
        <div
          v-if="isInteracting"
          class="voice-softphone-dock__shield"
          :class="isMoving ? 'cursor-grabbing' : 'cursor-nwse-resize'"
          data-testid="softphone-shield"
          aria-hidden="true"
        />
        <button
          type="button"
          class="voice-softphone-dock__resize group"
          data-testid="softphone-resize"
          :aria-label="$t('VOICE_SOFTPHONE.RESIZE')"
          :title="$t('VOICE_SOFTPHONE.RESIZE')"
          @pointerdown="startResize"
          @dblclick="resetLayout"
        >
          <span
            class="voice-softphone-dock__resize-grip"
            :class="{ 'opacity-100': isResizing }"
            aria-hidden="true"
          >
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
              <line
                x1="8"
                y1="1"
                x2="1"
                y2="8"
                stroke="currentColor"
                stroke-width="1.25"
                stroke-linecap="round"
              />
              <line
                x1="4"
                y1="1"
                x2="1"
                y2="4"
                stroke="currentColor"
                stroke-width="1.25"
                stroke-linecap="round"
              />
            </svg>
          </span>
        </button>
        <header
          class="flex items-center justify-between gap-3 px-6 py-[15px] border-b border-slate-100 dark:border-slate-700"
        >
          <h2
            class="m-0 text-base font-medium text-slate-800 dark:text-slate-50 truncate"
          >
            {{ $t('VOICE_SOFTPHONE.TITLE') }}
          </h2>
          <div class="flex items-center gap-3">
            <button
              type="button"
              class="voice-softphone-dock__action cursor-grab active:cursor-grabbing"
              :class="{ 'cursor-grabbing': isMoving }"
              data-testid="softphone-move"
              :aria-label="$t('VOICE_SOFTPHONE.MOVE')"
              :title="$t('VOICE_SOFTPHONE.MOVE')"
              @pointerdown="startMove"
              @dblclick="resetLayout"
            >
              <svg
                width="14"
                height="18"
                viewBox="0 0 12 16"
                fill="currentColor"
                aria-hidden="true"
              >
                <circle cx="3" cy="3" r="1.25" />
                <circle cx="9" cy="3" r="1.25" />
                <circle cx="3" cy="8" r="1.25" />
                <circle cx="9" cy="8" r="1.25" />
                <circle cx="3" cy="13" r="1.25" />
                <circle cx="9" cy="13" r="1.25" />
              </svg>
            </button>
            <button
              type="button"
              class="voice-softphone-dock__action"
              data-testid="softphone-open-window"
              :disabled="!softphone"
              :aria-label="$t('VOICE_SOFTPHONE.OPEN_WINDOW')"
              :title="$t('VOICE_SOFTPHONE.OPEN_WINDOW')"
              @click="openInWindow"
            >
              <fluent-icon icon="open" size="18" />
            </button>
            <button
              type="button"
              class="voice-softphone-dock__action"
              data-testid="softphone-minimize"
              :aria-label="$t('VOICE_SOFTPHONE.MINIMIZE')"
              :title="$t('VOICE_SOFTPHONE.MINIMIZE')"
              @click="toggle"
            >
              <fluent-icon icon="dismiss" size="18" />
            </button>
          </div>
        </header>
        <div
          v-if="isLoading"
          class="flex-1 flex items-center justify-center text-sm text-slate-600 dark:text-slate-300"
          data-testid="softphone-loading"
        >
          {{ $t('VOICE_SOFTPHONE.LOADING') }}
        </div>
        <iframe
          v-if="isFrameMounted"
          :src="softphoneUrl"
          :title="$t('VOICE_SOFTPHONE.TITLE')"
          class="voice-softphone-dock__frame flex-1 w-full border-0"
          :class="{ 'pointer-events-none': isInteracting }"
          allow="microphone; autoplay"
          data-testid="softphone-frame"
        />
        <p
          class="m-0 px-6 py-3 text-xs text-slate-600 dark:text-slate-300 border-t border-slate-100 dark:border-slate-700"
        >
          {{ $t(hintKey, hintParams) }}
          <button
            v-if="isFallback"
            type="button"
            class="ml-1 underline text-woot-500 hover:text-woot-600"
            data-testid="softphone-retry"
            @click="retryLogin"
          >
            {{ $t('VOICE_SOFTPHONE.RETRY') }}
          </button>
        </p>
      </div>
      <button
        type="button"
        class="voice-softphone-dock__launcher flex items-center justify-center w-12 h-12 rounded-full bg-woot-500 text-white shadow-lg hover:bg-woot-600"
        data-testid="softphone-toggle"
        :aria-expanded="isOpen ? 'true' : 'false'"
        :aria-label="launcherLabel"
        :title="launcherLabel"
        @click="toggle"
      >
        <fluent-icon :icon="isOpen ? 'chevron-down' : 'call'" size="22" />
      </button>
    </template>
  </div>
</template>

<style scoped lang="scss">
.voice-softphone-dock__launcher {
  position: fixed;
  right: 5rem;
  bottom: 4rem;
  z-index: 40;
}

.voice-softphone-dock__panel {
  position: fixed;
  z-index: 40;
  max-width: calc(100vw - 1rem);
  max-height: calc(100vh - 1rem);
}

.voice-softphone-dock__shield {
  position: fixed;
  inset: 0;
  z-index: 50;
}

.voice-softphone-dock__action {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 0.5rem;
  border: 0;
  background: transparent;
  color: var(--s-600);
  transition:
    background-color 0.15s,
    color 0.15s;

  svg {
    width: 1.125rem;
    height: 1.125rem;
  }

  &:hover,
  &:focus-visible {
    background: var(--s-75);
    color: var(--s-900);
  }

  .dark & {
    color: var(--s-300);
  }

  .dark &:hover,
  .dark &:focus-visible {
    background: var(--s-700);
    color: var(--s-25);
  }
}

.voice-softphone-dock__resize {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 10;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  width: 1.75rem;
  height: 1.75rem;
  padding: 0;
  cursor: nwse-resize;
  background: transparent;
  border: 0;

  &:focus {
    outline: none;
  }
}

.voice-softphone-dock__resize-grip {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  height: 1.25rem;
  border-bottom-right-radius: 0.375rem;
  color: var(--s-500);
  opacity: 0.3;
  transition: opacity 0.15s;
  pointer-events: none;

  .group:hover &,
  .group:focus-visible & {
    opacity: 1;
    background: var(--w-75);
  }
}

.voice-softphone-dock__frame {
  min-height: 0;
}
</style>
